import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { pickVisibleEl } from './onboardingDom';

/**
 * Focus overlay for the onboarding tour.
 *
 * Three modes, decided from the current step's target:
 *  - 'lock'  : no on-page target (practice steps 2-5, 6, or off-screen). The
 *              whole app is dimmed AND click-blocked; only the coachmark is live.
 *  - 'guide' : the step's real control is on screen (step 1 "Add item",
 *              step 7 Qora input). A lighter, NON-blocking dim with a hole +
 *              pulsing halo points at it; the page stays usable so the user can
 *              fill the form / read the reply.
 *  - 'open'  : a target is expected but not found right now (e.g. the user
 *              drilled into the create form). No dim — get out of the way and
 *              keep the coachmark as guidance.
 */

export type SpotlightMode = 'lock' | 'guide' | 'open';

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Which way the user has scrolled the highlighted control out of view. */
export type SpotlightOffscreen = 'above' | 'below' | null;

interface Layout {
  mode: SpotlightMode;
  hole: Rect | null;
  coach: { top: number; left: number };
  offscreen: SpotlightOffscreen;
}

interface OnboardingSpotlightProps {
  targetSelector?: string;
  padding?: number;
  // 'auto' positions the coachmark next to the spotlight (or bottom-center when
  // there is no target). 'top-right' pins it to the top-right corner — used for
  // the welcome bridge. 'above' keeps it above the target so a downward-opening
  // dropdown (e.g. the Species combobox) is never covered.
  placement?: 'auto' | 'top-right' | 'above';
  // When false, the page is not dimmed or blocked — only the halo (if any) and
  // the coachmark show. Use for "look at this" steps (the report, Qora's reply)
  // where the user must read the live screen.
  dim?: boolean;
  // When true (default), the dimmed area also BLOCKS clicks — only the spotlight
  // hole (the highlighted element) stays interactive, so the user can do nothing
  // but the one thing we're pointing at. Set false where the rest of the screen
  // must stay usable (e.g. a dropdown that renders outside the hole).
  blockOutside?: boolean;
  // A second selector whose visible matches are merged into the spotlight hole,
  // so an open dropdown rendered below the field is neither dimmed nor blocked.
  // Re-checked each frame, so it only widens the hole while the dropdown is open.
  unionSelector?: string;
  // Always scroll the target to the vertical centre when it changes (not only
  // when off-screen), leaving room to place the coachmark above it.
  centerTarget?: boolean;
  children: (ctx: {
    mode: SpotlightMode;
    /** Set while the highlight sits outside the viewport, so a coach card can
     *  offer a way back instead of pointing at nothing. */
    offscreen: SpotlightOffscreen;
    /** Bring the highlighted control back to the centre of the screen. */
    recenter: () => void;
  }) => React.ReactNode;
}

const Z_BLOCK = 9990;
const Z_HALO = 9991;
const Z_COACH = 9993;
const GAP = 14;
const MARGIN = 16;
const SCRIM_LOCK = 'rgba(28, 22, 15, 0.72)';
const SCRIM_GUIDE = 'rgba(28, 22, 15, 0.45)';

function rectsClose(a: Rect | null, b: Rect | null): boolean {
  if (!a || !b) return a === b;
  return (
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
}

export const OnboardingSpotlight: React.FC<OnboardingSpotlightProps> = ({
  targetSelector,
  padding = 8,
  placement = 'auto',
  dim = true,
  blockOutside = true,
  unionSelector,
  centerTarget = false,
  children,
}) => {
  const coachRef = useRef<HTMLDivElement>(null);
  // Latest hole rect, readable from the capture-phase blocker below without
  // re-subscribing its listeners on every frame the geometry shifts.
  const holeRef = useRef<Rect | null>(null);
  // Which element we have already scrolled to. Keyed on the resolved NODE, not
  // just the selector: the cupping step points two consecutive fields at the
  // same selector, which resolves to the first roast's slider row and then the
  // second roast's. A selector-only guard counted that as "already scrolled" and
  // left the halo waiting off screen on a page the user had to find by hand.
  const scrolledFor = useRef<{ selector: string; el: HTMLElement } | null>(null);
  const [layout, setLayout] = useState<Layout | null>(null);

  useLayoutEffect(() => {
    let raf = 0;

    const compute = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const cw = coachRef.current?.offsetWidth ?? 340;
      const ch = coachRef.current?.offsetHeight ?? 160;

      let mode: SpotlightMode = 'lock';
      let hole: Rect | null = null;

      if (targetSelector) {
        const el = pickVisibleEl(targetSelector);
        const r = el?.getBoundingClientRect();
        if (el && r && r.width > 0 && r.height > 0) {
          mode = 'guide';
          let top = r.top;
          let left = r.left;
          let right = r.right;
          let bottom = r.bottom;
          // Merge any open dropdown so it stays outside the dim/lock.
          if (unionSelector) {
            document.querySelectorAll<HTMLElement>(unionSelector).forEach((u) => {
              const ur = u.getBoundingClientRect();
              if (ur.width > 0 && ur.height > 0) {
                top = Math.min(top, ur.top);
                left = Math.min(left, ur.left);
                right = Math.max(right, ur.right);
                bottom = Math.max(bottom, ur.bottom);
              }
            });
          }
          // True viewport coordinates, negatives included. Clamping top/left to
          // 0 without shrinking width/height (as this did) does not keep the
          // hole on screen — it MOVES it: scroll the target above the fold and
          // a full-size, undimmed rectangle stayed glued to the top-left corner,
          // sitting over the header as a highlight pointing at nothing. The
          // scrim quadrants below clamp their own sizes instead, which is where
          // the clamp belongs.
          hole = {
            top: top - padding,
            left: left - padding,
            width: right - left + padding * 2,
            height: bottom - top + padding * 2,
          };
          if (scrolledFor.current?.selector !== targetSelector || scrolledFor.current.el !== el) {
            if (centerTarget || r.top < 0 || r.bottom > vh) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            scrolledFor.current = { selector: targetSelector, el };
          }
        } else {
          // expected but not on this screen — step aside
          mode = 'open';
        }
      }

      // Report a highlight the user has scrolled past. MARGIN of it still
      // showing counts as on screen — a halo peeking at the edge is findable.
      let offscreen: SpotlightOffscreen = null;
      if (mode === 'guide' && hole) {
        if (hole.top + hole.height < MARGIN) offscreen = 'above';
        else if (hole.top > vh - MARGIN) offscreen = 'below';
      }

      let cTop: number;
      let cLeft: number;
      if (placement === 'top-right') {
        cTop = 84;
        cLeft = vw - cw - 24;
      } else if (placement === 'above' && mode === 'guide' && hole) {
        const above = hole.top - GAP - ch;
        cTop = above >= MARGIN ? above : hole.top + hole.height + GAP; // flip below only if no room
        cLeft = hole.left + hole.width / 2 - cw / 2;
      } else if (mode === 'guide' && hole) {
        const below = hole.top + hole.height + GAP;
        if (below + ch <= vh - MARGIN) {
          cTop = below;
        } else {
          const above = hole.top - GAP - ch;
          cTop = above >= MARGIN ? above : Math.max(MARGIN, Math.min(vh - ch - MARGIN, hole.top));
        }
        cLeft = hole.left + hole.width / 2 - cw / 2;
      } else {
        // No on-screen target to point at (practice/report steps in 'lock' mode):
        // pin the coachmark to the top-right corner — matching the welcome bridge —
        // instead of floating it at the bottom-centre.
        cTop = 84;
        cLeft = vw - cw - 24;
      }
      cLeft = Math.max(MARGIN, Math.min(cLeft, vw - cw - MARGIN));
      cTop = Math.max(MARGIN, Math.min(cTop, vh - ch - MARGIN));

      holeRef.current = hole;

      setLayout((prev) => {
        if (
          prev &&
          prev.mode === mode &&
          prev.offscreen === offscreen &&
          rectsClose(prev.hole, hole) &&
          Math.abs(prev.coach.top - cTop) < 0.5 &&
          Math.abs(prev.coach.left - cLeft) < 0.5
        ) {
          return prev;
        }
        return { mode, hole, coach: { top: cTop, left: cLeft }, offscreen };
      });

      raf = requestAnimationFrame(compute);
    };

    raf = requestAnimationFrame(compute);
    return () => cancelAnimationFrame(raf);
  }, [targetSelector, padding, placement, unionSelector, centerTarget]);

  // Headless UI's Dialog closes on any pointerup that lands outside its panel,
  // and it listens in the CAPTURE phase on `document`. The coachmark renders
  // outside that panel, so a click on its buttons (e.g. the field guide's
  // "Next") would otherwise close the very modal we are guiding. Intercept on
  // `window` — which captures BEFORE `document` — and stop coachmark pointer
  // events from reaching Headless UI's listener. (Plain `click` is untouched,
  // so the buttons' own onClick handlers still fire.)
  useEffect(() => {
    const stopForCoach = (e: Event) => {
      const t = e.target as Node | null;
      if (t && coachRef.current?.contains(t)) e.stopPropagation();
    };
    const events = ['pointerdown', 'pointerup', 'touchstart', 'touchend'];
    events.forEach((ev) => window.addEventListener(ev, stopForCoach, true));
    return () => events.forEach((ev) => window.removeEventListener(ev, stopForCoach, true));
  }, []);

  const mode: SpotlightMode = layout?.mode ?? (targetSelector ? 'open' : 'lock');
  const hole = layout?.hole ?? null;
  const offscreen = layout?.offscreen ?? null;
  const recenter = () => {
    if (!targetSelector) return;
    pickVisibleEl(targetSelector)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const blocking = dim && (mode === 'lock' || (mode === 'guide' && blockOutside));

  // Refuse every pointer interaction outside the spotlight hole while blocking.
  // Capture on `window` so nothing in the app — or in a dialog stacked above the
  // scrim — ever sees the event. Deliberately does NOT touch wheel, touchmove or
  // scroll: reading the page is always allowed, only acting on it is not.
  useEffect(() => {
    if (!blocking) return;
    const allowed = (e: Event, x: number, y: number) => {
      // The guide presses real controls on the user's behalf — a Back target's
      // Cancel button, for one — through HTMLElement.click(). That dispatches an
      // UNTRUSTED event whose clientX/clientY are both 0 and whose target sits
      // inside the form, not the coach card, so both tests below fail and the
      // tour's own action is refused: `refuse` stops it at this window capture
      // listener and the button's own onClick never runs. That is what left Back
      // enabled but dead on the field-walking phases of the cupping, packing and
      // sales-order create modals — the review-only phases escaped it only
      // because they pass dim={false}, which leaves `blocking` off entirely.
      // The comment on that call in OnboardingFieldGuide used to be right —
      // blocking was once a scrim with pointer-events, which a scripted .click()
      // ignores — but it moved here and quietly took those Back buttons with it.
      // This gate exists to stop the USER from acting outside the highlight, not
      // to stop the tour from driving the page, and only a real interaction
      // carries isTrusted.
      if (!e.isTrusted) return true;
      const t = e.target as Node | null;
      if (t && coachRef.current?.contains(t)) return true;
      const h = holeRef.current;
      if (!h) return false;
      return x >= h.left && x <= h.left + h.width && y >= h.top && y <= h.top + h.height;
    };
    const refuse = (e: Event) => {
      const pe = e as PointerEvent;
      if (allowed(e, pe.clientX, pe.clientY)) return;
      e.stopPropagation();
      // stopPropagation alone leaves NATIVE defaults intact, and a range input
      // still drags on its own. Cancel those too — but never for touch, where
      // preventDefault on pointerdown cancels the scroll gesture with it. Touch
      // therefore still allows a native drag on a control outside the halo; the
      // guided tour is a desktop flow, and keeping the page scrollable matters
      // more than closing that gap.
      if (pe.pointerType !== 'touch') e.preventDefault();
    };
    const events = ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'click', 'dblclick'];
    events.forEach((ev) => window.addEventListener(ev, refuse, true));
    return () => events.forEach((ev) => window.removeEventListener(ev, refuse, true));
  }, [blocking]);

  const scrim = blocking ? SCRIM_LOCK : SCRIM_GUIDE;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0;

  // The scrims are PAINT ONLY — they never receive events. They are fixed
  // children of <body>, but the app scrolls inside <main> (DashboardLayout is
  // h-screen + overflow-hidden), so a scrim with pointer-events:auto sat outside
  // that scroll container: a wheel over it chained to <html>, which does not
  // scroll, and the page froze mid-step with no way back up. The capture-phase
  // listener above does the blocking instead — it refuses interaction while
  // leaving wheel and touch scrolling to the page.
  const blockerBase: React.CSSProperties = {
    position: 'fixed',
    background: scrim,
    zIndex: Z_BLOCK,
    pointerEvents: 'none',
  };

  // Render at <body> level, NOT inside #root. When a Headless UI Dialog opens it
  // marks #root as `inert`; anything inside (the coachmark) would become
  // unclickable and clicks would fall through it. A body-level sibling escapes
  // that, and its high z-index still stacks above the dialog.
  return createPortal(
    <>
      {dim && mode === 'lock' && (
        <div
          className="onboarding-scrim"
          style={{ ...blockerBase, inset: 0 }}
          data-test-id="onboarding-scrim"
          aria-hidden
        />
      )}

      {mode === 'guide' && hole && (
        <>
          {dim && (
            <>
              <div
                className="onboarding-scrim"
                style={{ ...blockerBase, top: 0, left: 0, width: vw, height: Math.max(0, hole.top) }}
                      aria-hidden
              />
              <div
                className="onboarding-scrim"
                style={{
                  ...blockerBase,
                  top: hole.top + hole.height,
                  left: 0,
                  width: vw,
                  height: Math.max(0, vh - (hole.top + hole.height)),
                }}
                      aria-hidden
              />
              <div
                className="onboarding-scrim"
                style={{
                  ...blockerBase,
                  top: hole.top,
                  left: 0,
                  width: Math.max(0, hole.left),
                  height: hole.height,
                }}
                      aria-hidden
              />
              <div
                className="onboarding-scrim"
                style={{
                  ...blockerBase,
                  top: hole.top,
                  left: hole.left + hole.width,
                  width: Math.max(0, vw - (hole.left + hole.width)),
                  height: hole.height,
                }}
                      aria-hidden
              />
            </>
          )}
          <div
            className="onboarding-halo"
            style={{
              position: 'fixed',
              top: hole.top,
              left: hole.left,
              width: hole.width,
              height: hole.height,
              borderRadius: 10,
              zIndex: Z_HALO,
              pointerEvents: 'none',
            }}
            aria-hidden
          />
        </>
      )}

      <div
        ref={coachRef}
        className="onboarding-coach"
        style={{
          position: 'fixed',
          top: layout?.coach.top ?? -9999,
          left: layout?.coach.left ?? -9999,
          width: 'min(360px, calc(100vw - 32px))',
          zIndex: Z_COACH,
          pointerEvents: 'auto',
          visibility: layout ? 'visible' : 'hidden',
        }}
      >
        {children({ mode, offscreen, recenter })}
      </div>
    </>,
    document.body,
  );
};
